import { OTPVerificationScreen } from '@/components/organisms/OTPVerificationScreen';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { useProfile } from '@/modules/auth/hooks/useProfile';
import { useOTP } from '@/modules/onboarding/verification/otp/hooks/useOTP';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';

export default function ProfileEmailOTPInline() {
    const params = useLocalSearchParams();
    const router = useRouter();
    const user = useAppStore((state) => state.auth.user);
    const { fetchProfile } = useProfile();

    const progress = params.progress ? parseFloat(params.progress as string) : 0.8;

    // For edit feature, start with empty contactInfo to allow editing
    // User can enter/change the email before submitting
    const [contactInfo, setContactInfo] = useState<string>('');
    const [showOTP, setShowOTP] = useState<boolean>(false); // Start with false to allow editing

    // Get customerId from user (authenticated user) for OTP service
    const customerId = user?.customer_id || null;

    const {
        isLoading: isVerifyingOTP,
        error: otpError,
        isVerified,
        verifyOtp,
        resendOtp,
        clearError,
        sendOtp,
    } = useOTP({
        type: 'email',
        contactInfo,
        autoSend: false, // We'll send manually when contact is submitted
        updateType: 'Secondary', // Communication email from profile uses Secondary
    });

    const [isSendingOTP, setIsSendingOTP] = useState(false);

    const handleContactSubmit = useCallback((contact: string) => {
        logger.info('ProfileEmailOTPInline', 'Email submitted, sending OTP', { contact });
        setContactInfo(contact);
        // Show OTP input immediately (don't wait for API response)
        setShowOTP(true);
        setIsSendingOTP(true);
        // Send OTP in background (don't await - let it run async)
        sendOtp(contact)
            .then(() => {
                logger.info('ProfileEmailOTPInline', 'OTP sent successfully');
            })
            .catch((error) => {
                logger.error('ProfileEmailOTPInline', 'Failed to send OTP', error);
            })
            .finally(() => {
                setIsSendingOTP(false);
            });
    }, [sendOtp]);

    const handleOTPComplete = useCallback(async (otp: string) => {
        logger.info('ProfileEmailOTPInline', 'Email OTP entered, verifying...', { otp });
        const verified = await verifyOtp(otp);

        if (verified) {
            logger.info('ProfileEmailOTPInline', 'OTP verified, refreshing profile before navigating', {
                email: contactInfo,
            });

            try {
                // Wait for profile API to complete before navigating to ensure UI updates instantly
                await fetchProfile();
                logger.info('ProfileEmailOTPInline', 'Profile refreshed successfully, navigating back');
            } catch (error) {
                logger.error('ProfileEmailOTPInline', 'Failed to refresh profile after email update', error);
                // Still navigate back even if API fails - user can see updated data on next app restart
            }

            // Navigate back after profile is refreshed
            router.back();
        }

        return verified;
    }, [verifyOtp, contactInfo, router, fetchProfile]);

    const handleResendOTP = useCallback(async () => {
        logger.info('ProfileEmailOTPInline', 'Resend OTP requested');
        clearError();
        await resendOtp();
    }, [resendOtp, clearError]);

    // Don't pass contactInfo prop to allow editing
    // The component will initialize with empty string, and user can enter/change the value
    return (
        <OTPVerificationScreen
            type="email"
            mode="inline"
            progress={progress}
            showOTP={showOTP}
            loading={isSendingOTP}
            verifyingOTP={isVerifyingOTP}
            otpError={otpError}
            otpSuccess={isVerified}
            onContactSubmit={handleContactSubmit}
            onOTPComplete={handleOTPComplete}
            onResendOTP={handleResendOTP}
            onOTPChange={clearError}
            updateType="Secondary"
        />
    );
}

